import { isSlateValueEmpty } from './richFormatterUtils.js';

/**
 * Validates if required fields in goal data are empty
 * @param {Object} goalData - The goal data object
 * @param {Array} steps - The steps array
 * @param {Function} setEmptyField - State setter for field errors
 * @returns {boolean} - True if there are empty required fields
 */
export const validateGoalFields = (goalData, steps, setEmptyField) => {
    let hasEmptyFields = false;
    const fieldsToCheckWhenApproval = [
        'goal_name',
        'goal_term',
        'goal_importance',
        'goals_etro',
        'relationship',
        'target_date',
        'steps'
    ];

    // Draft goals only require goal_name
    if (goalData.goal_status === 'Draft Goal') {
        if (!goalData.goal_name) {
            setEmptyField(prevState => ({
                ...prevState,
                goal_name: { error: true, errorText: 'Field cannot be empty' }
            }));
            return true;
        }
        return false;
    }

    // Non-draft goals require all fields
    fieldsToCheckWhenApproval.forEach(field => {
        // Validate steps
        if (field === 'steps') {
            if (!steps || (Array.isArray(steps) && !steps[0]?.step)) {
                setEmptyField(prevState => ({
                    ...prevState,
                    [field]: { error: true, errorText: 'Field cannot be empty' }
                }));
                hasEmptyFields = true;
            }
            return;
        }

        // Validate rich text fields (goal_importance, relationship)
        if (['goal_importance', 'relationship'].includes(field)) {
            if (isSlateValueEmpty(goalData[field])) {
                setEmptyField(prevState => ({
                    ...prevState,
                    [field]: { error: true, errorText: 'Field cannot be empty' }
                }));
                hasEmptyFields = true;
            }
            return;
        }

        // Validate other fields
        if (!goalData[field] || (Array.isArray(goalData[field]) && !goalData[field].length)) {
            setEmptyField(prevState => ({
                ...prevState,
                [field]: { error: true, errorText: 'Field cannot be empty' }
            }));
            hasEmptyFields = true;
        }
    });

    return hasEmptyFields;
};

/**
 * Determines if the goal form should be disabled based on user role and goal status
 * @param {Object} params - Configuration object
 * @param {Array} goals - All goals array
 * @param {string|number} goalId - Current goal ID
 * @param {boolean} isGoalSaved - Whether the goal is saved
 * @param {boolean} fromModal - Whether form is displayed in a modal
 * @param {Object} loggedUser - Currently logged in user
 * @param {boolean} isCoach - Whether logged user is a coach
 * @param {Array} userPeopleBuilder - User's people builder data
 * @returns {boolean} - True if form should be disabled
 */
export const shouldDisableGoalForm = ({
    goals,
    goalId,
    isGoalSaved,
    fromModal,
    loggedUser,
    isCoach,
    userPeopleBuilder
}) => {
    const goalSelected = goals?.find(goal => goal.id == goalId);

    // Allow editing if goal is not saved yet
    if (!isGoalSaved) {
        return false;
    }

    // Disable if displayed in modal
    if (fromModal) {
        return true;
    }

    // Admins and coaches can always edit
    if (loggedUser.is_lifecycle_admin) {
        return false;
    }

    // Coach-specific logic
    if (isCoach) {
        // Active goals cannot be edited by coach
        if (goalSelected?.goal_status === 'Active') {
            return true;
        }

        // Allow editing if people builder is active and goal is not in approval states
        if (
            userPeopleBuilder?.[0]?.pb_settings.status === 'active' &&
            goalSelected?.goal_status !== 'Active' &&
            goalSelected?.goal_status !== 'Approval' &&
            goalSelected?.goal_status !== 'Ready for Approval'
        ) {
            return false;
        }

        // Disable by default for coaches
        // return true;
    }

    // Players cannot edit goals in approval or active states
    else if (
        goalSelected?.goal_status === 'Approval' ||
        goalSelected?.goal_status === 'Ready for Approval' ||
        goalSelected?.goal_status === 'Active'
    ) {
        return true;
    }

    return false;
};

/**
 * Filters out empty or undefined fields from goal data
 * @param {Object} goalData - The goal data object
 * @returns {Object} - Filtered goal data with only non-empty values
 */
export const filterEmptyFields = (goalData) => {
    return Object.fromEntries(
        Object.entries(goalData).filter(([objKey, v]) => {
            // Always include these fields even if empty
            if (objKey === 'goal_approved_date' || objKey === 'comments') {
                return true;
            }

            // Filter out empty values
            return (
                v !== undefined &&
                v !== null &&
                v !== '' &&
                (typeof v !== 'object' || Object.keys(v).length !== 0)
            );
        })
    );
};

/**
 * Prepares goal data for saving to API
 * @param {Object} goalData - The goal data object
 * @param {Array} goalComments - The goal comments array
 * @returns {Object} - Goal data ready for API submission
 */
export const prepareGoalDataForSave = (goalData, goalComments) => {
    return {
        ...goalData,
        comments: goalComments ? JSON.stringify(goalComments) : '[]'
    };
};

/**
 * Prepares goal data for update to API (filters empty fields and serializes comments)
 * @param {Object} goalData - The goal data object
 * @param {Array} goalComments - The goal comments array
 * @returns {Object} - Filtered goal data ready for API update
 */
export const prepareGoalDataForUpdate = (goalData, goalComments) => {
    const filteredData = filterEmptyFields(goalData);
    return {
        ...filteredData,
        comments: JSON.stringify(goalComments)
    };
};

/**
 * Clears validation error for a specific field
 * @param {Object} emptyField - Current empty field state
 * @param {string} fieldName - Name of the field to clear error for
 * @returns {Object} - Updated empty field state
 */
export const clearFieldError = (emptyField, fieldName) => {
    return {
        ...emptyField,
        [fieldName]: { error: false, errorText: '' }
    };
};
